import { LitElement, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { MorningCheckStateController } from '../../services/morning-check-state.js';
import type { DailyTask, WeatherData } from '../../services/morning-check-api.js';

@customElement('cp-morning-check')
export class CpMorningCheck extends LitElement {
  createRenderRoot() { return this; }

  private _state = new MorningCheckStateController(this);
  private _cachedSummaryRaw: string | null = null;
  private _cachedSummary: { completedTasks: number; totalTasks: number; weeklyProgress?: Array<{ goal: string; progress: string }> } | null = null;

  connectedCallback() {
    super.connectedCallback();
    void this._state.load();
  }

  private _onTriggerClick() {
    void this._state.triggerDailyCheck();
  }

  private _renderEmpty() {
    return html`
      <div class="cp-morning-check__empty">
        <p class="cp-morning-check__empty-text">Noch kein Morning Check vorhanden</p>
        <button class="cp-morning-check__trigger-btn" @click=${this._onTriggerClick}>
          Jetzt Morning Check starten
        </button>
      </div>
    `;
  }

  private _renderWeather(weather: WeatherData | null) {
    if (!weather) {
      return html`
        <div class="cp-morning-check__section cp-morning-check__weather">
          <h3 class="cp-morning-check__section-title">Wetter</h3>
          <p class="cp-morning-check__weather-unavailable">Wetter aktuell nicht verfuegbar</p>
        </div>
      `;
    }

    const icon = this._getWeatherIcon(weather.condition);
    return html`
      <div class="cp-morning-check__section cp-morning-check__weather">
        <h3 class="cp-morning-check__section-title">Wetter</h3>
        <div class="cp-morning-check__weather-content">
          <span class="cp-morning-check__weather-icon">${icon}</span>
          <div class="cp-morning-check__weather-details">
            <span class="cp-morning-check__weather-temp">${weather.temperature}\u00B0C</span>
            <span class="cp-morning-check__weather-condition">${weather.condition}</span>
          </div>
        </div>
      </div>
    `;
  }

  private _getWeatherIcon(condition: string): string {
    const lower = condition.toLowerCase();
    if (lower.includes('sonn') || lower.includes('clear') || lower.includes('sun')) return '\u2600';
    if (lower.includes('wolke') || lower.includes('cloud') || lower.includes('bewoel')) return '\u2601';
    if (lower.includes('regen') || lower.includes('rain')) return '\u2602';
    if (lower.includes('schnee') || lower.includes('snow')) return '\u2744';
    if (lower.includes('gewitter') || lower.includes('thunder')) return '\u26A1';
    return '\u2600';
  }

  private _renderYesterdaySummary() {
    const data = this._state.data;
    if (!data?.yesterday_summary) return nothing;

    if (data.yesterday_summary !== this._cachedSummaryRaw) {
      try {
        this._cachedSummary = JSON.parse(data.yesterday_summary);
        this._cachedSummaryRaw = data.yesterday_summary;
      } catch {
        this._cachedSummary = null;
        this._cachedSummaryRaw = data.yesterday_summary;
      }
    }

    const summary = this._cachedSummary;
    if (!summary) return nothing;

    const percent = summary.totalTasks > 0
      ? Math.round((summary.completedTasks / summary.totalTasks) * 100)
      : 0;

    return html`
      <div class="cp-morning-check__section cp-morning-check__yesterday">
        <h3 class="cp-morning-check__section-title">Gestern</h3>
        <div class="cp-morning-check__yesterday-stats">
          <span class="cp-morning-check__yesterday-text">
            Yesterday: ${summary.completedTasks}/${summary.totalTasks} Tasks erledigt (${percent}%)
          </span>
        </div>
        <div class="cp-morning-check__progress-bar">
          <div class="cp-morning-check__progress-fill" style="width: ${percent}%"></div>
        </div>
        ${summary.weeklyProgress && summary.weeklyProgress.length > 0 ? html`
          <div class="cp-morning-check__weekly-progress">
            <h4 class="cp-morning-check__subsection-title">Wochenziel-Fortschritt</h4>
            ${summary.weeklyProgress.map(wp => html`
              <div class="cp-morning-check__weekly-item">
                <span class="cp-morning-check__weekly-goal">${wp.goal}</span>
                <span class="cp-morning-check__weekly-status">${wp.progress}</span>
              </div>
            `)}
          </div>
        ` : nothing}
      </div>
    `;
  }

  private _renderCalendar() {
    const data = this._state.data;
    if (!data?.calendar_data || data.calendar_data.length === 0) return nothing;

    return html`
      <div class="cp-morning-check__section cp-morning-check__calendar">
        <h3 class="cp-morning-check__section-title">Termine</h3>
        <ul class="cp-morning-check__calendar-list">
          ${data.calendar_data.map(entry => html`
            <li class="cp-morning-check__calendar-item">
              <span class="cp-morning-check__calendar-time">${entry.time}</span>
              <span class="cp-morning-check__calendar-title">${entry.title}</span>
            </li>
          `)}
        </ul>
      </div>
    `;
  }

  private _getPriorityClass(priority: string): string {
    switch (priority.toLowerCase()) {
      case 'high': return 'cp-morning-check__task-priority--high';
      case 'medium': return 'cp-morning-check__task-priority--medium';
      case 'low': return 'cp-morning-check__task-priority--low';
      default: return '';
    }
  }

  private _renderTasks(tasks: DailyTask[]) {
    if (tasks.length === 0) return nothing;

    return html`
      <div class="cp-morning-check__section cp-morning-check__tasks">
        <h3 class="cp-morning-check__section-title">Tages-Tasks (${tasks.length})</h3>
        <ul class="cp-morning-check__task-list">
          ${tasks.map(task => html`
            <li class="cp-morning-check__task-item">
              <div class="cp-morning-check__task-header">
                <span class="cp-morning-check__task-title">${task.title}</span>
                <span class="cp-morning-check__task-priority ${this._getPriorityClass(task.priority)}">
                  ${task.priority}
                </span>
              </div>
              <p class="cp-morning-check__task-description">${task.description}</p>
              <div class="cp-morning-check__task-meta">
                ${task.project_assignment ? html`
                  <span class="cp-morning-check__task-tag">${task.project_assignment}</span>
                ` : html`
                  <span class="cp-morning-check__task-tag cp-morning-check__task-tag--personal">${task.source_goal}</span>
                `}
                ${task.estimated_minutes ? html`
                  <span class="cp-morning-check__task-time">${task.estimated_minutes} Min</span>
                ` : nothing}
              </div>
            </li>
          `)}
        </ul>
      </div>
    `;
  }

  render() {
    const { loadingState, error, data, triggerState } = this._state;

    if (loadingState === 'loading' && !data) {
      return html`
        <div class="cp-page cp-morning-check">
          <h1 class="cp-page__title">Morning Check</h1>
          <div class="cp-morning-check__loading">Lade Morning Check...</div>
        </div>
      `;
    }

    if (error) {
      return html`
        <div class="cp-page cp-morning-check">
          <h1 class="cp-page__title">Morning Check</h1>
          <div class="cp-morning-check__error">${error}</div>
        </div>
      `;
    }

    if (loadingState === 'loaded' && !data) {
      return html`
        <div class="cp-page cp-morning-check">
          <h1 class="cp-page__title">Morning Check</h1>
          ${this._renderEmpty()}
        </div>
      `;
    }

    if (!data) return nothing;

    const isTriggering = triggerState === 'triggering';

    return html`
      <div class="cp-page cp-morning-check">
        <div class="cp-morning-check__header">
          <h1 class="cp-page__title" style="margin:0">Morning Check</h1>
          <div class="cp-morning-check__header-actions">
            ${!data.isCurrent ? html`
              <span class="cp-morning-check__stale-hint">Letzter Check: ${data.date}</span>
            ` : nothing}
            <button
              class="cp-morning-check__trigger-btn"
              ?disabled=${isTriggering}
              @click=${this._onTriggerClick}
            >
              ${isTriggering ? 'Wird ausgefuehrt...' : 'Morning Check erneut ausfuehren'}
            </button>
          </div>
        </div>

        <div class="cp-morning-check__grid">
          ${this._renderYesterdaySummary()}
          ${this._renderWeather(data.weather_data)}
          ${this._renderCalendar()}
          ${this._renderTasks(data.daily_tasks)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-morning-check': CpMorningCheck;
  }
}
